<template>
  <div
    :class="[
      'bg-theme-sidebar text-theme-sidebar-text w-64 space-y-6 py-40 md:py-7 px-2 absolute inset-y-0 left-0 transform',
      isOpen ? 'translate-x-0' : '-translate-x-full',
      'md:relative md:translate-x-0 transition-all duration-300 ease-in-out z-40 md:border-r md:border-theme-border',
    ]"
  >
    <div class="flex items-center justify-between px-4">
      <h2 class="text-2xl font-semibold text-secondary-500 transition-colors duration-300">Dashboard</h2>
      <button @click="toggleSidebar" class="md:hidden text-theme-sidebar-text transition-colors duration-300">
        <i class="pi pi-times text-xl"></i>
      </button>
    </div>

    <nav class="px-2 space-y-1">
      <router-link
        v-for="item in menuItems"
        :key="item.name"
        :to="item.path"
        @click="handleNavigation"
        class="flex items-center gap-3 py-3 px-4 rounded-lg transition-all duration-200 text-theme-sidebar-text/80 hover:text-theme-sidebar-text"
        :class="{ 'bg-secondary-500/20 text-secondary-500 font-semibold': $route.path === item.path }"
      >
        <i :class="['pi', item.icon, 'text-secondary-500 transition-colors duration-300']" />

        <span class="flex items-center gap-2">
          {{ item.name }}

          <!-- ✅ Notification badge ONLY for Chats — shows number of chats with unread -->
          <span
            v-if="item.path === '/new-profile/chats' && chatsWithUnread > 0"
            class="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full inline-flex items-center justify-center"
          >
            {{ chatsWithUnread > 9 ? '9+' : chatsWithUnread }}
          </span>
        </span>
      </router-link>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/store/auth';
import { useChatStore } from '@/store/chat'; // ✅ added

const isOpen = ref(false);
const userStore = useAuthStore();
const user = computed(() => userStore.user);

// ✅ added: unread count from chat store
const chatStore = useChatStore();
const { unreadCounts } = storeToRefs(chatStore);

// Count how many chats have at least 1 unread message
const chatsWithUnread = computed(() =>
  Object.values(unreadCounts.value).filter((c) => c > 0).length
);

const menuItems = computed(() => {
  const baseItems = [
    { name: 'My Account', path: '/new-profile/account', icon: 'pi-user' },
    { name: 'Chats', path: '/new-profile/chats', icon: 'pi-comments' },
    { name: 'Statistics', path: '/new-profile/statistics', icon: 'pi-chart-bar' },
    { name: 'Settings', path: '/new-profile/settings', icon: 'pi-cog' },
  ];

  const roleItem =
    user.value?.role === 'lessee'
      ? { name: 'Orders', path: '/new-profile/orders', icon: 'pi-shopping-cart' }
      : user.value?.role === 'lessor'
      ? { name: 'Items', path: '/new-profile/items', icon: 'pi-box' }
      : null;

  return roleItem ? [baseItems[0], roleItem, ...baseItems.slice(1)] : baseItems;
});

const toggleSidebar = () => {
  isOpen.value = !isOpen.value;
};

const handleToggle = () => {
  isOpen.value = !isOpen.value;
};

const handleNavigation = () => {
  if (window.innerWidth < 768) {
    isOpen.value = false;
  }
};

onMounted(async () => {
  window.addEventListener('toggle-sidebar', handleToggle);

  // ✅ added: make unread count update globally
  if (userStore.user) {
    await chatStore.fetchChats();          // get initial count immediately
    chatStore.startChatsPolling(4000);     // keep it updated
  }
});

onUnmounted(() => {
  window.removeEventListener('toggle-sidebar', handleToggle);

  // ✅ added: stop polling
  chatStore.stopChatsPolling();
});
</script>

<style scoped>
.pi {
  transition: color 0.3s ease;
}

a {
  background-color: transparent;
  position: relative;
  overflow: hidden;
}

a::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-secondary-500) / 0.1;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  z-index: -1;
}

a:hover {
  background-color: var(--color-surfaceHover, rgba(255, 255, 255, 0.08));
  border-radius: 0.5rem;
}

a:hover::before {
  transform: translateX(0);
}

a:hover .pi {
  color: var(--color-secondary-500);
}

/* Active link styling */
a.router-link-active {
  background: var(--color-secondary-500) / 0.15 !important;
}

a.router-link-active::before {
  transform: translateX(0);
}
</style>
